/**
 * Selectors — cards, chart series, and stage ledger rows.
 * All derived from the authoritative monthly ledger. No independent finance math.
 */

import { nearlyEqual, n } from './math.js';
import { stageCashInNote, stagePaymentsDueNote } from './paymentEvents.js';

/**
 * Four main conclusions from the ledger.
 */
export function selectConclusions(ledger, pnl, assumptions = {}) {
  if (!ledger || ledger.incomplete || !ledger.months?.length) {
    return {
      incomplete: true,
      lowestCash: null,
      additionalFunding: null,
      peakSponsorCash: null,
      completionCash: null,
    };
  }

  const months = ledger.months;
  let lowest = months[0];
  for (const row of months) {
    if (row.closingSpvCash < lowest.closingSpvCash) lowest = row;
  }

  // Additional funding required: peak funding gap, with source hint and month.
  let peakGap = months[0];
  for (const row of months) {
    if (row.fundingGap > peakGap.fundingGap) peakGap = row;
  }
  const facilityGap = ledger.facilityAnalysis?.remainingBankFundingGap || 0;
  const constructionFacilityRequired = Math.max(
    0,
    Number(ledger.facilityAnalysis?.constructionFacilityRequired || 0),
  );
  const cashGap = peakGap.fundingGap;
  const alreadySecuredLandFacility = Math.max(
    0,
    Number(ledger.facilityAnalysis?.alreadySecuredLandFacility || 0),
  );
  // Construction credit is structural in VAT and is modelled in the cash path even
  // before it is committed. Keep its commitment gap separate from any residual
  // sponsor cash gap, then show the full land + construction debt requirement.
  const additionalAmount = Math.max(0, facilityGap + cashGap);
  const totalDebtRequired = Math.max(
    0,
    alreadySecuredLandFacility + constructionFacilityRequired,
  );
  let source = 'none';
  if (facilityGap > 0 && cashGap > 0) source = 'bank + sponsor';
  else if (facilityGap > 0) source = 'bank';
  else if (cashGap > 0) source = 'sponsor';

  // Peak sponsor cash injected = opening equity + cumulative contributions at peak.
  let peakSponsor = ledger.openingEquity;
  let peakSponsorMonth = 1;
  let running = ledger.openingEquity;
  for (const row of months) {
    running += row.spvContribution;
    if (running > peakSponsor) {
      peakSponsor = running;
      peakSponsorMonth = row.month;
    }
  }

  const last = months[months.length - 1];
  const distributed = ledger.totals.cumulativeDistributions || 0;
  const capitalInvested = n(pnl.capitalInvested);
  // Preserve losses — never replace negative profit with zero.
  const netProfit = n(pnl.netProfit);
  const closing = last.closingSpvCash;
  const sponsorContributions = n(ledger.totals.totalSpvContributions);
  // Feasibility is a genuine cost outside the P&L profit line; Fees funding is not.
  const feasibilityPaid = Math.max(0, n(pnl.feasibilityCosts));
  // Completion explanation from actual contributions + net result − out-of-profit
  // genuine costs − distributions. Fees funding is never deducted as an expense.
  const expectedCompletionCash =
    sponsorContributions + netProfit - feasibilityPaid - distributed;
  const pnlHeadlineCapitalPlusProfit = capitalInvested + netProfit;

  // Without distributions, completion cash holds the residual (subject to funding gaps).
  let capitalAvailable = 0;
  let profitAvailable = 0;
  if (distributed <= 0) {
    capitalAvailable = Math.min(Math.max(0, closing), Math.max(0, capitalInvested));
    profitAvailable = closing - capitalAvailable;
  } else {
    capitalAvailable = Math.max(0, capitalInvested - distributed);
    profitAvailable = netProfit - Math.max(0, distributed - capitalInvested);
  }

  return {
    incomplete: false,
    lowestCash: {
      amount: lowest.closingSpvCash,
      month: lowest.month,
      stage: lowest.stage,
    },
    additionalFunding: {
      amount: additionalAmount,
      source,
      month: facilityGap >= peakGap.fundingGap && facilityGap > 0
        ? ledger.facilityAnalysis.facilityMustBeAvailableMonth
        : peakGap.month,
      cashGap,
      bankFundingGap: facilityGap,
      totalDebtRequired: Math.round(totalDebtRequired),
      alreadySecuredLandFacility: Math.round(alreadySecuredLandFacility),
      additionalToSecure: Math.round(additionalAmount),
    },
    peakSponsorCash: {
      amount: peakSponsor,
      month: peakSponsorMonth,
    },
    completionCash: {
      cashRemainingInSpv: closing,
      sponsorCapitalAvailableToReturn: capitalAvailable,
      profitAvailable,
      cashAlreadyDistributed: distributed,
      // If undistributed, proceeds still sit in SPV cash; if distributed, proceeds are the cash paid out.
      totalSponsorProceeds: assumptions.distributeAtCompletion
        ? distributed
        : closing,
      /** Explanation from contributions + net result − distributions (losses preserved). */
      bridge: {
        sponsorContributions,
        capitalInvested,
        pnlNetProfit: netProfit,
        pnlHeadlineCapitalPlusProfit,
        feasibilityPaid,
        cashAlreadyDistributed: distributed,
        expectedCompletionCash,
        reconciles: nearlyEqual(closing, expectedCompletionCash, 100),
      },
    },
  };
}

/**
 * Chart series: positive bars (SPV / bank / buyer), negative bars (costs / repayments),
 * closing cash line. No cumulative sales-as-cash lines.
 */
export function selectChartSeries(ledger, salesSchedule) {
  if (!ledger?.months?.length) return { bars: [], line: [], milestones: [] };

  const bars = ledger.months.map((row) => ({
    month: row.month,
    stage: row.stage,
    spvIn: row.spvContribution,
    bankPartnerIn: row.securedBankPartnerIn,
    buyerIn: row.usableBuyerCash,
    costsOut: -row.spvCostsPaid,
    repaymentsOut: -(row.repayments + row.sponsorDistribution),
  }));

  const line = ledger.months.map((row) => ({
    month: row.month,
    closingSpvCash: row.closingSpvCash,
    salesSecured: row.salesSecured,
  }));

  const milestones = (salesSchedule?.milestones || []).map((m) => ({
    pct: m.pct,
    offsetFromAuthorization: m.offsetFromAuthorization,
  }));

  return { bars, line, milestones };
}

/**
 * Stage ledger rows — grouped monthly rows. Each row must satisfy North Star.
 */
export function selectStageRows(ledger) {
  if (!ledger?.months?.length) return [];

  const stageOrder = ['preparation', 'authorization', 'construction', 'delivery', 'complete'];
  const byStage = new Map();

  for (const row of ledger.months) {
    if (!byStage.has(row.stage)) byStage.set(row.stage, []);
    byStage.get(row.stage).push(row);
  }

  const rows = [];
  for (const stage of stageOrder) {
    const group = byStage.get(stage);
    if (!group?.length) continue;
    const first = group[0];
    const last = group[group.length - 1];
    const sum = (key) => group.reduce((s, r) => s + n(r[key]), 0);

    const openingCash = first.openingSpvCash;
    const spvIn = sum('spvContribution');
    const bankPartnersIn = sum('securedBankPartnerIn');
    const buyerCashIn = sum('usableBuyerCash');
    const costsOut = sum('spvCostsPaid');
    const repayments = sum('repayments') + sum('sponsorDistribution');
    const directLandDebtRepayment = sum('buyerDirectLandSettlement');
    const closingCash = last.closingSpvCash;
    const expected = openingCash + spvIn + bankPartnersIn + buyerCashIn - costsOut - repayments;

    const costDetail = aggregateCostDetail(group);

    rows.push({
      stage,
      openingCash,
      spvIn,
      bankPartnersIn,
      buyerCashIn,
      costsOut,
      repayments,
      directLandDebtRepayment,
      projectedConstructionFacilityDraw: sum('projectedConstructionFacilityDraw'),
      closingCash,
      equationBalances: nearlyEqual(expected, closingCash, 0.05),
      fundingSecured: group.every(
        (row) => row.closingSpvCash >= -0.05
          && n(row.projectedConstructionFacilityDraw) <= 0.05,
      ),
      // Expandable detail
      months: group,
      costDetail,
      bankDebtOutstanding: last.bankDebtOutstanding,
      restrictedBuyerCash: last.restrictedBuyerCash,
      buyerDirectConstruction: sum('buyerDirectConstruction'),
      buyerDirectLandSettlement: sum('buyerDirectLandSettlement'),
      unmetBankConditions: [...new Set(group.flatMap((r) => r.unmetBankConditions || []))],
      // Bank-statement fields (primary stage table)
      cashInTotal: spvIn + bankPartnersIn + buyerCashIn,
      // Unique atomic sources once (not concatenated monthly note strings).
      cashInNote: stageCashInNote({
        spvContribution: spvIn,
        landFacilityDraw: sum('landFacilityDraw'),
        constructionFacilityDraw: sum('constructionFacilityDraw'),
        securedPartnerDraw: sum('securedPartnerDraw'),
        usableBuyerCash: buyerCashIn,
      }),
      paymentsDue: costsOut,
      // Concise stage summary — month-by-month events stay on monthly rows.
      paymentsDueNote: stagePaymentsDueNote(stage, costDetail),
      debtRepaid: repayments,
      landDebtOutstanding: last.landDebtOutstanding,
      constructionDebtOutstanding: last.constructionDebtOutstanding,
    });
  }
  return rows;
}

function aggregateCostDetail(group) {
  const out = {};
  for (const row of group) {
    for (const [k, v] of Object.entries(row.costDetail || {})) {
      out[k] = (out[k] || 0) + v;
    }
  }
  return out;
}
